import * as Yup from 'yup'
import Ajax from "../../../Ajax"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import Field from "../../form/fields/Field"
import { Alert, AlertModal } from "../../.."
import { store } from "../../../stores/Store"
import ViewQuestionnaire from "./ViewQuestionnaire"
import { Button } from "@mui/material"
import { SelectOption } from '../../form/fields/SelectField'


interface IQuestionnaireImportFormProps {
    importFromFile?: boolean
}

interface FileWithMetadata extends File {
    type: string;
    size: number;
}


export default observer(function QuestionnaireImportForm ({importFromFile}: IQuestionnaireImportFormProps) {

    const formik = useFormik({

        initialValues: {
            file: null,
            displayName: "",
            respondentEmail: "",
            advisorUid: ""
        },

        validationSchema: Yup.object({
            file: importFromFile ? null : Yup.mixed<FileWithMetadata>()
                .required('A file is required')
                .test('fileFormat', "You must provide a .json file", value => {
                    return value && value.type === "application/json"
                })
                .test('fileSize', "You are limitted to files of 1MB in size", value => {
                    return value && value.size <= 1000000; // 200KB in bytes
                }),
            displayName: Yup.string().matches(/^[ ,.@!$%#';:?()[\]~_+=À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$/u, "You have used some illegal characters").required("This field is required"),
            respondentEmail: Yup.string().matches(
                /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/,
                "A valid email is required"
            ).required("This field is required"),
            advisorUid: Yup.string().required()
        }),

        onSubmit: async (values, actions) => {
            
            var formData = new FormData()
            formData.append("file", values.file)

            return await Ajax.Questionnaire.Import(formData, values.advisorUid, values.respondentEmail, values.displayName).then((response) => {
                store.QuestionnaireStore.getQuestionnaires()
                store.AlertStore.Close()
                AlertModal({width: "700px", body: <ViewQuestionnaire uid={response.data} />})
            }).catch((response) => {
                Alert({message: response.data?.detail, color: "error"})
            })
        }
    })

    var clientOptions: SelectOption[] = []
    // Add customer accounts
    store.NodeStore.accounts.filter((a) => !!a.advisorUid && ["customer"].includes(a.accountType)).forEach((a) => {
        clientOptions.push({value: a.advisorUid, label: a.label})
    })

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div className="form-header">
                <div>
                    <h2>Import Questionnaire</h2>
                    <p>This form requires an exported questionnaire in the form of a .json file.</p>
                </div>
            </div>

            <div className="form-fields" style={{maxWidth: "270px"}}>
                <Field.Text
                    formik
                    name="displayName"
                    label="Reference/label"
                    helpText="This can be the name of the applicant or job. The applicant won't see this."
                    onChange={formik.handleChange}
                    value={formik.values.displayName}
                    warning={formik.submitCount && formik.errors.displayName ? formik.errors.displayName : false} />

                <Field.Text
                    formik
                    name="respondentEmail"
                    label="Respondent contact email"
                    helpText="This is for your own record, we won't contact or share this email with anyone."
                    onChange={formik.handleChange}
                    value={formik.values.respondentEmail}
                    warning={formik.submitCount && formik.errors.respondentEmail ? formik.errors.respondentEmail : false} />

                <Field.Select
                    formik
                    label="Assign to a Sponsor (optional)"
                    helpText="Enables filtering and allows authorised sponsor users to view this questionnaire."
                    name="advisorUid"
                    onChange={formik.handleChange}
                    value={formik.values.advisorUid}
                    warning={formik.submitCount && formik.errors.advisorUid ? formik.errors.advisorUid : false}
                    options={clientOptions} />
                
                <Field.File
                    formik
                    name="file"
                    onChange={(e) => formik.setFieldValue("file", e.target.value)}
                    value={formik.values.file}
                    warning={formik.submitCount ? formik.errors.file : null} />
            </div>

            <div className="form-buttons">
                <Button 
                    className="btn btn-sm" 
                    onClick={() => store.AlertStore.Close()}>
                    Cancel
                </Button>
                
                <Button 
                    className="btn btn-sm" 
                    type="submit" 
                    variant="contained" 
                    color="success" 
                    disabled={formik.isSubmitting}>
                    Create
                </Button>
            </div>
        </form>
    )
})